// The partial-invite rule, pure (contract 22 §14; ORP-18889). ~40 lines of decision that v3 spread
// across a `.then` and a `.catch` (`UserPermissionModal.vue:506-527`).
//
// THE RULE: `results[]` is the answer, not the HTTP status. The bulk create endpoint returns the
// same array on 2xx and on 4xx — a 400 whose body carries `results` means "some of these landed",
// which is a RESULT SCREEN, not an error banner. Only a response with no `results` at all is a
// failure. Everything here is framework-free so both halves are testable without a transport.
import type { InviteResultDto, InviteResultStatus } from '../models/memberInvite.types'

import { settingsWriteFailure } from './settingsWriteOutcome'

import { ApiError } from '@/shared/http'

/** The bulk endpoint's body, in both its 2xx and its 4xx form. */
interface InviteResultsBody {
  results?: unknown
}

const KNOWN_STATUSES: readonly string[] = ['success', 'already_invited', 'error']

/**
 * Coerce a wire status. An unrecognised word becomes `'error'`, never `'success'`: BE-22-08 may add
 * statuses, and the safe direction is to under-claim delivery. (Retrying an invite is harmless;
 * telling someone an invitation was sent when it was not is the defect `3152f6e0` fixed.)
 */
function toStatus(value: unknown): InviteResultStatus {
  return typeof value === 'string' && KNOWN_STATUSES.includes(value)
    ? (value as InviteResultStatus)
    : 'error'
}

function toResult(entry: unknown): InviteResultDto | null {
  if (!entry || typeof entry !== 'object') return null
  const { email, status, error } = entry as Record<string, unknown>
  if (typeof email !== 'string' || email === '') return null
  return {
    email,
    status: toStatus(status),
    ...(typeof error === 'string' && error !== '' ? { error } : {}),
  }
}

/**
 * Read `results[]` out of a response body. `null` means the key was absent — the caller decides
 * what that means (a 2xx synthesises success; a 4xx is a real failure).
 */
export function toInviteResults(body: unknown): InviteResultDto[] | null {
  if (!body || typeof body !== 'object') return null
  const { results } = body as InviteResultsBody
  if (!Array.isArray(results)) return null
  return results.map(toResult).filter((row): row is InviteResultDto => row !== null)
}

/** The same read, against a thrown `ApiError`'s captured 4xx body. */
export function inviteResultsFromError(cause: unknown): InviteResultDto[] | null {
  if (!ApiError.isApiError(cause)) return null
  const results = toInviteResults(cause.payload)
  return results && results.length > 0 ? results : null
}

export interface InviteSummary {
  invited: number
  already: number
  failed: number
  /** Every row succeeded — v3's `allInvited` (`:352`), which gates the green header. */
  allInvited: boolean
}

export function summarizeInvites(results: readonly InviteResultDto[]): InviteSummary {
  const invited = results.filter((row) => row.status === 'success').length
  return {
    invited,
    already: results.filter((row) => row.status === 'already_invited').length,
    failed: results.filter((row) => row.status === 'error').length,
    allInvited: results.length > 0 && invited === results.length,
  }
}

/** The addresses a Retry should put back in the field. Only `'error'` — an `already_invited`
 *  address is not retryable, and re-sending it would produce the identical row again. */
export function failedInviteEmails(results: readonly InviteResultDto[]): string[] {
  return results.filter((row) => row.status === 'error').map((row) => row.email)
}

/** The typed seat refusal (ORP-21470). Ratified with the MCP-only wave; the backend emits it on
 *  `POST /accounts/{a}/users/create` when the plan's seats are gone. */
export const SEAT_LIMIT_CODE = 'INTERACTION_SEAT_LIMIT_REACHED'

/** The AI-assistant (MCP) seat refusal — a grant that turns the assistant ON with no seat left
 *  (`common/api_contracts/error_codes.py` `ASSISTANT_SEATS_EXCEEDED`). Distinct from the plan's
 *  user seats above: the account may have room for the person and none for their assistant. */
export const ASSISTANT_SEATS_CODE = 'ASSISTANT_ACCESS_SEATS_EXCEEDED'

/**
 * Which sentence a failed invite/update gets — the branch `MemberInviteModal` used to hold inline.
 *
 * ⚠ THE 403 IS AMBIGUOUS and was the reason that copy hedged: v3 assumed every 403 was a seat
 * limit and opened the export-limit dialog (`UserPermissionModal.vue:379-381`). Now that the
 * backend has a MACHINE CODE for the seat case, the code is read FIRST and outranks the HTTP
 * status — a coded refusal is not a guess, and it is the only branch allowed to say "no seats
 * left". Everything else keeps the hedge.
 *
 * @param errorKey what `settingsWriteFailure` classified the cause as
 * @param cause the original rejection, for its machine code
 * @param isEditing a grant edit fails with different copy than a bulk invite
 */
export function inviteFailureKey(errorKey: string, cause: unknown, isEditing: boolean): string {
  const prefix = 'workspaceSettings.permissions.invite'
  if (ApiError.isApiError(cause) && cause.code === SEAT_LIMIT_CODE) return `${prefix}.seatLimit`
  if (ApiError.isApiError(cause) && cause.code === ASSISTANT_SEATS_CODE) {
    return `${prefix}.assistantSeatLimit`
  }
  if (errorKey === 'workspaceSettings.error.forbidden') return `${prefix}.refused`
  return `${prefix}.${isEditing ? 'updateFailed' : 'failed'}`
}

/**
 * A rejected invite/edit as the modal's inline key, or `undefined` for a canceled request.
 * Reuses `settingsWriteFailure`'s classification (canceled ⇒ silent, 403 ⇒ refusal); which
 * sentence a real failure gets is `inviteFailureKey`'s. Whatever it returns, the modal KEEPS the
 * chips and nothing navigates or opens a second modal — v3 answered every 403 with the
 * EXPORT-limit dialog (`UserPermissionModal.vue:379-381`).
 */
export function classifyInviteFailure(cause: unknown, isEditing: boolean): string | undefined {
  const outcome = settingsWriteFailure(cause)
  if (outcome.ok || outcome.errorKey === null) return undefined
  return inviteFailureKey(outcome.errorKey, cause, isEditing)
}
